import { Field, ImageField, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { styled } from '@mui/material/styles';
import { useEffect, useRef } from 'react';
import { ComponentProps } from 'lib/component-props';
import {
  SITE_MASTER_COLORS,
  StSiteMasterSection,
  StSiteMasterStack,
} from 'components/SiteMaster/SiteMaster.styles';
import {
  formatSiteAddress,
  getDirectionsUrl,
  getFieldValue,
  useSiteMasterRouteFields,
} from 'components/SiteMaster/utils';
import UiBox from 'components/shared/Box';
import ButtonSecondary from 'components/shared/Button/SecondaryButton';
import { StNextImage } from 'components/shared/NextImage.styles';
import UiText from 'components/shared/Typography/UiText';

const StSiteLocationSectionTitle = styled('h3')(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 700,
  color: SITE_MASTER_COLORS.teal,
  fontFamily: theme.typography.fontFamily,
  letterSpacing: 0,
}));

const renderContactIcon = (field: ImageField | undefined): JSX.Element =>
  field?.value?.src ? (
    <StNextImage
      field={field}
      width={20}
      height={20}
      alt={typeof field.value.alt === 'string' ? field.value.alt : ''}
    />
  ) : (
    <></>
  );

type SiteLocationDetailProps = ComponentProps & {
  fields: {
    sectionTitle?: Field<string>;
    getDirectionButtonText?: Field<string>;
    locationIcon?: ImageField;
    phoneIcon?: ImageField;
    mapLink?: Field<string>;
  };
};

const isGoogleMapsApiReady = (): boolean =>
  typeof window !== 'undefined' && typeof window.google?.maps?.Map === 'function';

const SiteLocationDetailMap = ({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);

  useEffect(() => {
    let cancelled = false;
    let pollId = 0;
    let idleListener: google.maps.MapsEventListener | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const position = (): google.maps.LatLngLiteral => ({ lat: latitude, lng: longitude });

    const placeMarker = (map: google.maps.Map) => {
      markerRef.current?.setMap(null);
      markerRef.current = new google.maps.Marker({
        map,
        position: position(),
      });
    };

    const initMap = () => {
      const container = containerRef.current;
      if (cancelled || !container || !isGoogleMapsApiReady()) {
        return;
      }

      if (!mapRef.current) {
        mapRef.current = new google.maps.Map(container, {
          center: position(),
          zoom: 14,
          scrollwheel: false,
          mapTypeControl: false,
          streetViewControl: true,
          streetViewControlOptions: {
            position: google.maps.ControlPosition.LEFT_BOTTOM,
          },
          fullscreenControl: true,
          zoomControl: false,
          draggable: true,
          disableDefaultUI: true,
          cameraControl: true,
        });

        idleListener = google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
          if (!cancelled && mapRef.current) {
            placeMarker(mapRef.current);
          }
        });
      } else {
        mapRef.current.setCenter(position());
        placeMarker(mapRef.current);
      }

      google.maps.event.trigger(mapRef.current, 'resize');
    };

    const waitForApi = () => {
      if (cancelled) {
        return;
      }

      if (isGoogleMapsApiReady()) {
        initMap();
        return;
      }

      pollId = window.setTimeout(waitForApi, 50);
    };

    waitForApi();

    const container = containerRef.current;
    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (cancelled) {
          return;
        }

        if (mapRef.current) {
          google.maps.event.trigger(mapRef.current, 'resize');
          if (!markerRef.current) {
            placeMarker(mapRef.current);
          }
        } else if (isGoogleMapsApiReady()) {
          initMap();
        }
      });
      resizeObserver.observe(container);
    }

    return () => {
      cancelled = true;
      window.clearTimeout(pollId);
      idleListener?.remove();
      resizeObserver?.disconnect();
      markerRef.current?.setMap(null);
      markerRef.current = null;
      mapRef.current = null;
    };
  }, [latitude, longitude]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
};

const SiteLocationDetail = (props: SiteLocationDetailProps): JSX.Element => {
  const routeFields = useSiteMasterRouteFields();
  const { fields } = props;
  const address = formatSiteAddress(routeFields);
  const phone = getFieldValue(routeFields.sitePhone);
  const directionsUrl = getDirectionsUrl(routeFields, getFieldValue(fields.mapLink));
  const latRaw = getFieldValue(routeFields.siteLatitude);
  const lngRaw = getFieldValue(routeFields.siteLongitude);
  const latitude = latRaw ? Number(latRaw) : NaN;
  const longitude = lngRaw ? Number(lngRaw) : NaN;
  const hasMapCoordinates =
    Number.isFinite(latitude) && Number.isFinite(longitude) && (latitude !== 0 || longitude !== 0);

  if (!hasMapCoordinates) return <></>;
  return (
    <StSiteMasterSection sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <StSiteLocationSectionTitle id="heading" sx={{ marginBottom: 0, lineHeight: '100%' }}>
        <Text field={fields.sectionTitle} />
      </StSiteLocationSectionTitle>

      <StSiteMasterStack sx={{ gap: 2 }}>
        {address && (
          <UiBox
            styles={{
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start',
              width: '100%',
            }}
          >
            <UiBox
              styles={{
                flexShrink: 0,
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {renderContactIcon(fields.locationIcon)}
            </UiBox>
            <UiBox styles={{ flex: 1, wordBreak: 'break-word' }}>
              <UiText size="caption" color="black">
                {address}
              </UiText>
            </UiBox>
          </UiBox>
        )}

        {phone && (
          <UiBox
            styles={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <UiBox
              styles={{
                flexShrink: 0,
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {renderContactIcon(fields.phoneIcon)}
            </UiBox>
            <UiBox styles={{ flex: 1 }}>
              <UiText size="caption" color="black">
                <a
                  href={`tel:${phone}`}
                  rel="noreferrer noopener"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  target="_blank"
                >
                  {phone}
                </a>
              </UiText>
            </UiBox>
          </UiBox>
        )}
      </StSiteMasterStack>

      {hasMapCoordinates && (
        <UiBox
          styles={{
            width: '100%',
            height: 290,
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <SiteLocationDetailMap latitude={latitude} longitude={longitude} />
        </UiBox>
      )}

      {directionsUrl && (
        <ButtonSecondary href={directionsUrl} fullWidth target="_blank" rel="noopener noreferrer">
          <Text field={fields.getDirectionButtonText} />
        </ButtonSecondary>
      )}
    </StSiteMasterSection>
  );
};

export default SiteLocationDetail;
